import AlertError from '@material-ui/icons/ErrorOutlineOutlined'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { Button } from 'opensolar-ui'
import { useCallback, useEffect, useRef } from 'react'
import { SaveButton, Toolbar, useNotify, useRedirect, useTranslate } from 'react-admin'
import { Link } from 'react-router-dom'
import { ActionType, EventType } from 'types/tracking'
import { capitalize } from 'util/misc'

type AdditionalTrackingData =
  | Record<string, unknown>
  | ((submittedValues: Record<string, any>, response: { data: Record<string, any> }) => Record<string, unknown>)

const TrackedToolbar = (
  props: any & {
    additionalTrackingData?: AdditionalTrackingData
    beforeOnSave?: (saveFunc: Function) => Promise<boolean> // return false to abort
    /** Runs after the record was persisted (update path uses `response.data.modified_date`). */
    afterSaveSuccess?: (response: { data: Record<string, any> }, submittedValues: Record<string, any>) => void
  }
) => {
  const notify = useNotify()
  const redirect = useRedirect()
  const translate = useTranslate()
  const { trackEvent } = useTrackComponent({
    componentKey: `control.${props.resource}`,
    eventName: `User Interacted With ${props.resource}`,
  })

  const afterSaveSuccessRef = useRef(props.afterSaveSuccess)
  useEffect(() => {
    afterSaveSuccessRef.current = props.afterSaveSuccess
  }, [props.afterSaveSuccess])

  const onSuccess = useCallback((response, submittedValues, finalRedirect) => {
    afterSaveSuccessRef.current?.(response, submittedValues)
    const record = response.data
    const eventRecordData = { data: { type: props.resource, id: record.id, title: record.title } }

    const additionalData =
      typeof props.additionalTrackingData === 'function'
        ? props.additionalTrackingData(submittedValues, response)
        : props.additionalTrackingData || {}

    if (!record.modified_date) {
      trackEvent(
        EventType.SYSTEM_EVENT,
        { type: ActionType.SAVE },
        {
          eventName: `User Created ${capitalize(props.resource)}`,
          ...eventRecordData,
          ...additionalData,
        }
      )
      notify('ra.notification.created', 'info')
      redirect(finalRedirect, `/${props.resource}`, record.id, record)
    } else {
      trackEvent(
        EventType.SYSTEM_EVENT,
        { type: ActionType.UPDATE },
        {
          eventName: `User Edited ${capitalize(props.resource)}`,
          ...eventRecordData,
          ...additionalData,
        }
      )
      notify('ra.notification.updated', 'success')
      redirect(finalRedirect, `/${props.resource}`)
    }
  }, [])

  const redirectLinkOnCancel =
    props.redirect && props.redirect !== 'list' && props.redirect !== 'edit' ? props.redirect : `/${props.resource}`

  return (
    <Toolbar {...props}>
      {!props.hideCancelButton && (
        <Link style={{ textDecoration: 'none' }} to={redirectLinkOnCancel}>
          <Button
            startIcon={<AlertError />}
            variant="contained"
            color="default"
            size="large"
            style={{ margin: '10px 0 10px 10px' }}
          >
            <span>{translate('Cancel')}</span>
          </Button>
        </Link>
      )}
      <SaveButton
        {...props}
        redirect={props.redirect}
        submitOnEnter={props.submitOnEnter}
        handleSubmitWithRedirect={props.handleSubmitWithRedirect || props.handleSubmit}
        onClick={() => {
          trackEvent(
            EventType.USER_INTERACTION,
            { type: ActionType.CLICK },
            {
              eventName: `User Clicked On ${capitalize(props.resource)} Save Button`,
            }
          )
        }}
        onSave={async (values: object, redirect: string, form: any, defaultSaveFunc: Function) => {
          defaultSaveFunc(values, redirect, {
            onSuccess: (response) => onSuccess(response, values, redirect),
            onFailure: props.onFailure,
          })
        }}
      />
    </Toolbar>
  )
}

export default TrackedToolbar
